import { Injectable, signal } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  EndSessionPopupRequest,
  EndSessionRequest,
  EventMessage,
  EventType,
  IPublicClientApplication,
  InteractionRequiredAuthError,
  PopupRequest,
  RedirectRequest,
} from '@azure/msal-browser';
import { Observable, firstValueFrom, from } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';
import { RuntimeConfigService } from '../config/runtime-config.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private initialized = false;
  private initializationPromise: Promise<void> | null = null;
  private msalClientReady: Promise<void> | null = null;
  private readonly inactivityLogoutInProgress = signal(false);
  private interactionInProgress = false;

  // Refresh a little before the access token actually expires so an active user never issues a
  // request with a stale token.
  private static readonly RENEWAL_BUFFER_MS = 5 * 60 * 1000;
  private static readonly MIN_RENEWAL_DELAY_MS = 30 * 1000;
  private renewalTimer: ReturnType<typeof setTimeout> | null = null;
  private proactiveRenewalStarted = false;

  constructor(
    private readonly msalService: MsalService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly runtimeConfigService: RuntimeConfigService
  ) {
    this.registerLoginSuccessHook();
    this.initializationPromise = this.initialize();
  }

  get instance(): IPublicClientApplication {
    return this.msalService.instance;
  }

  get msalSubject$() {
    return this.msalBroadcastService.msalSubject$;
  }

  get inProgress$() {
    return this.msalBroadcastService.inProgress$;
  }

  handleRedirectObservable(): Observable<AuthenticationResult | null> {
    return this.msalService.handleRedirectObservable();
  }

  async ready(): Promise<void> {
    if (!this.initializationPromise) {
      this.initializationPromise = this.initialize();
    }

    return this.initializationPromise;
  }

  loginRedirect(request?: RedirectRequest): Observable<void> {
    return from(this.ensureClientInitialized()).pipe(
      switchMap(() => this.msalService.loginRedirect(request))
    );
  }

  loginPopup(request?: PopupRequest): Observable<AuthenticationResult> {
    return from(this.ensureClientInitialized()).pipe(
      switchMap(() => this.msalService.loginPopup(request))
    );
  }

  async login(options?: {
    usePopup?: boolean;
    request?: PopupRequest | RedirectRequest;
  }): Promise<void> {
    await this.ensureClientInitialized();

    if (options?.usePopup) {
      const result = await this.msalService.instance.loginPopup(
        options.request as PopupRequest | undefined
      );
      if (result?.account) {
        this.msalService.instance.setActiveAccount(result.account);
        this.handlePostLogin('LOGIN_POPUP');
      }
      return;
    }

    await this.msalService.loginRedirect(options?.request as RedirectRequest | undefined);
  }

  logoutRedirect(request?: EndSessionRequest): Observable<void> {
    return this.msalService.logoutRedirect(request);
  }

  logoutPopup(request?: EndSessionPopupRequest): Observable<void> {
    return this.msalService.logoutPopup(request);
  }

  async logout(options?: {
    usePopup?: boolean;
    request?: EndSessionPopupRequest | EndSessionRequest;
  }): Promise<void> {
    await this.ensureClientInitialized();

    if (options?.usePopup) {
      await this.msalService.logoutPopup(options.request as EndSessionPopupRequest | undefined);
      return;
    }

    const request = this.buildLogoutRequest(options?.request as EndSessionRequest | undefined);
    await this.msalService.logoutRedirect(request);
  }

  ensureActiveAccount(): void {
    let activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount) {
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length > 0) {
        this.msalService.instance.setActiveAccount(accounts[0]);
        activeAccount = accounts[0];
      }
    }

    if (activeAccount) {
      // Hook for custom logic to run once account becomes active
      this.logPostLogin('ENSURE_ACTIVE_ACCOUNT', activeAccount.username);
    }
  }

  async acquireToken(
    scopes: string[],
    options?: { forceRefresh?: boolean; allowInteraction?: boolean },
  ): Promise<string | null> {
    await this.ready();
    this.ensureActiveAccount();
    const account = this.msalService.instance.getActiveAccount();

    if (!account) {
      return null;
    }

    try {
      const result = await firstValueFrom(
        this.msalService.acquireTokenSilent({
          account,
          scopes,
          forceRefresh: options?.forceRefresh ?? false,
        }),
      );
      return result?.accessToken ?? null;
    } catch (error) {
      // A genuinely unrenewable session (expired refresh grant surfaces as a token-endpoint 400 /
      // invalid_grant, or as InteractionRequiredAuthError) can only be recovered interactively.
      if (this.requiresInteraction(error)) {
        console.warn('Silent token acquisition requires interaction', error);
        if (options?.allowInteraction) {
          this.acquireTokenInteractively(scopes, account);
        }
        return null;
      }

      console.error('acquireTokenSilent failed', error);
      return null;
    }
  }

  private requiresInteraction(error: unknown): boolean {
    if (error instanceof InteractionRequiredAuthError) {
      return true;
    }

    const errorCode = (error as { errorCode?: string } | null)?.errorCode ?? '';
    return [
      'invalid_grant',
      'interaction_required',
      'login_required',
      'consent_required',
      'token_renewal_error',
    ].includes(errorCode);
  }

  // Re-establishes tokens through MSAL's supported redirect flow. When the SSO session is still
  // valid this completes without a password prompt. Guarded so concurrent failing API calls cannot
  // launch multiple redirects; the flag resets naturally on the post-redirect page load.
  private acquireTokenInteractively(scopes: string[], account: AccountInfo): void {
    if (this.interactionInProgress) {
      return;
    }

    this.interactionInProgress = true;
    void this.msalService.instance
      .acquireTokenRedirect({ scopes, account })
      .catch((redirectError) => {
        this.interactionInProgress = false;
        console.error('Interactive token acquisition failed', redirectError);
      });
  }

  hasAuthenticatedAccount(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  async getCurrentAccount(): Promise<AccountInfo | null> {
    await this.ready();

    if (!this.hasAuthenticatedAccount()) {
      return null;
    }

    this.ensureActiveAccount();
    return this.msalService.instance.getActiveAccount();
  }

  handlePostLogin(source?: string): void {
    this.ensureActiveAccount();

    const activeAccount = this.msalService.instance.getActiveAccount();
    if (activeAccount) {
      this.logPostLogin(source ?? 'POST_LOGIN', activeAccount.username);
    }
  }

  private registerLoginSuccessHook(): void {
    this.msalBroadcastService.msalSubject$
      .pipe(filter((message: EventMessage) => message.eventType === EventType.LOGIN_SUCCESS))
      .subscribe(() => {
        this.handlePostLogin('LOGIN_SUCCESS_EVENT');
        this.startProactiveTokenRenewal();
      });
  }

  private async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    await this.ensureClientInitialized();
    this.ensureActiveAccount();
    this.initialized = true;

    if (this.hasAuthenticatedAccount()) {
      this.startProactiveTokenRenewal();
    }
  }

  // Keeps the access token fresh ahead of expiry for an active session, and re-checks whenever the
  // tab returns to the foreground (covering background throttling and machine sleep). This is a
  // best-effort silent renewal; genuine interaction requirements are still handled reactively by
  // the interceptor, so a failure here never interrupts the user.
  private startProactiveTokenRenewal(): void {
    if (typeof window === 'undefined' || this.proactiveRenewalStarted) {
      return;
    }

    this.proactiveRenewalStarted = true;
    document.addEventListener('visibilitychange', this.handleVisibilityRenewal);
    void this.renewAndSchedule();
  }

  private readonly handleVisibilityRenewal = () => {
    if (document.visibilityState === 'visible') {
      void this.renewAndSchedule();
    }
  };

  private async renewAndSchedule(): Promise<void> {
    await this.ready();

    if (!this.hasAuthenticatedAccount()) {
      return;
    }

    const scope = this.runtimeConfigService.scopes;
    if (!scope) {
      return;
    }

    this.ensureActiveAccount();
    const account = this.msalService.instance.getActiveAccount();
    if (!account) {
      return;
    }

    try {
      const result = await firstValueFrom(
        this.msalService.acquireTokenSilent({ account, scopes: [scope] }),
      );
      this.scheduleNextRenewal(result?.expiresOn ?? null);
    } catch (error) {
      // Leave recovery to the reactive 401/403 path; do not force interaction in the background.
      console.debug('[AuthService] Proactive token renewal skipped', error);
    }
  }

  private scheduleNextRenewal(expiresOn: Date | null): void {
    if (this.renewalTimer !== null) {
      clearTimeout(this.renewalTimer);
      this.renewalTimer = null;
    }

    if (!expiresOn) {
      return;
    }

    const delay = Math.max(
      expiresOn.getTime() - Date.now() - AuthService.RENEWAL_BUFFER_MS,
      AuthService.MIN_RENEWAL_DELAY_MS,
    );
    this.renewalTimer = setTimeout(() => void this.renewAndSchedule(), delay);
  }

  private async ensureClientInitialized(): Promise<void> {
    if (!this.msalClientReady) {
      const instance = this.msalService.instance as IPublicClientApplication & {
        _isInitialized?: boolean;
      };
      this.msalClientReady = (async () => {
        if (!instance._isInitialized) {
          await instance.initialize();
        }
      })();
    }

    await this.msalClientReady;
  }

  private logPostLogin(source: string, username: string): void {
    // Central spot for custom post-login logic without affecting current flows
    console.debug('[AuthService] Post-login hook triggered', { source, username });
  }

  // Inactivity is a pure application policy: once the user has been idle past the configured
  // window we end the session. This is intentionally independent of access-token lifetime — an
  // expired access token is renewed silently by MSAL and must never, on its own, log the user out.
  async logoutDueToInactivity(): Promise<void> {
    if (this.inactivityLogoutInProgress()) {
      return;
    }

    this.inactivityLogoutInProgress.set(true);
    try {
      if (!this.hasAuthenticatedAccount()) {
        return;
      }

      console.info('[AuthService] Inactivity logout initiated');
      await this.logout();
    } finally {
      this.inactivityLogoutInProgress.set(false);
    }
  }

  private buildLogoutRequest(request?: EndSessionRequest): EndSessionRequest | undefined {
    const postLogoutRedirectUri = request?.postLogoutRedirectUri ?? this.getLogoutRedirectTarget();
    const account = request?.account ?? this.msalService.instance.getActiveAccount() ?? undefined;

    if (!postLogoutRedirectUri && !account && !request) {
      return undefined;
    }

    return {
      ...request,
      account,
      postLogoutRedirectUri,
    };
  }

  private getLogoutRedirectTarget(): string | undefined {
    const configuredTarget = window.location.origin;

    if (configuredTarget) {
      return configuredTarget;
    }

    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin;
    }

    return undefined;
  }
}
